import { useEffect, useState } from "react";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { Box, Chip, FormControl, InputLabel, OutlinedInput } from "@mui/material";
import Grid from "@mui/material/Grid";
import { loadOwners, saveOwner, totalOwners } from "../services/owners.service";
import { Apartment, Contacts, Edit, Person, Visibility } from "@mui/icons-material";
import AddNewOrganization from "../components/modals/addNewOrganization";
import AddNewUser from "../components/modals/addNewUser";
import { issueResponseMessage, issueWarnMessage } from "../actions/message";
import { useSelector } from "react-redux";
import {
  isEmptyObject,
  isEmptyString,
  isNullUndefined,
  isValidPhoneNumber,
} from "../util/util";
import { MainContainer } from "../constants/component.constants";
import Permit from "../util/permit";
import MuiDataGrid from "../components/modules/MuiDataGrid";
import ViewOwnerModal from "../components/modals/homes/ownerships/viewOwnerModal";
import EditUserModal from "../components/modules/users/editUserModal";
import { getIdTypes } from "../services/list.service";
import { getUserRoles, saveUser } from "../services/users.service";
import { deepOrange, green } from "@mui/material/colors";

export default function Owners() {
  const user = useSelector((state) => state.auth.user);
  const [ownerName, setOwnerName] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });
  const [ownerList, setOwnerList] = useState([]);
  const [total, setTotalOwners] = useState(0);
  const [task, setTask] = useState("");
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [addOrganizationModal, setAddOrganizationModal] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);
  const [viewOwnerModal, setViewOwnerModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editCompanyContactModal, setEditCompanyContactModal] = useState(false);
  const [selectedCompanyContact, setSelectedCompanyContact] = useState();
  const [idTypes, setIdTypes] = useState([]);
  const [userRoles, setUserRoles] = useState([]);

  useEffect(() => {
    if (!user?.proxyFacility) return;
    let isMounted = true;
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchData = async () => {
        try {
          const [data, total] = await Promise.all([
            loadOwners(
              ownerName,
              paginationModel.page,
              paginationModel.pageSize,
            ),
            totalOwners(ownerName),
          ]);
          if (isMounted) {
            setOwnerList(data);
            setTotalOwners(total.total);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      };
      fetchData();
    }, 500);
    return () => {
      isMounted = false;
      clearTimeout(delayDebounceFn);
    };
  }, [
    ownerName,
    paginationModel.page,
    paginationModel.pageSize,
    user.proxyFacility,
  ]);

  useEffect(() => {
    getIdTypes().then((idTypes) => setIdTypes(idTypes));
    getUserRoles().then((userRoles) => setUserRoles(userRoles));
  }, []);

  function getChipProps(params) {
    if (!isNullUndefined(params.value)) {
      if (params.value === "Company") {
        return {
          icon: <Apartment style={{ fill: deepOrange[500] }} />,
          label: "Company",
          style: {
            borderColor: deepOrange[500],
          },
        };
      } else if (params.value === "Individual") {
        return {
          icon: <Person style={{ fill: green[500] }} />,
          label: "Individual",
          style: {
            borderColor: green[500],
          },
        };
      } else {
        return "N/A";
      }
    }
  }

  const columns = [
    { headerName: "Owner's Name", field: "name", minWidth: 250, flex: 2.5 },
    {
      field: "type",
      headerName: "Type",
      minWidth: 150,
      flex: 2,
      renderCell: (params) => {
        return (
          <Chip variant="outlined" size="small" {...getChipProps(params)} />
        );
      },
    },
    { headerName: "Property", field: "property", minWidth: 200, flex: 2 },
    { headerName: "Units Owned", field: "units", minWidth: 250, flex: 2.5 },
    {
      headerName: "Ownership Start",
      field: "titleStart",
      minWidth: 150,
      flex: 1.5,
    },
    {
      headerName: "Ownership End",
      field: "titleEnd",
      minWidth: 150,
      flex: 1.5,
    },
    { headerName: "Phone", field: "mobilePhone", minWidth: 150, flex: 1.5 },
    { headerName: "Email", field: "email", minWidth: 175 },
    {
      headerName: "Actions",
      field: "actions",
      width: 100,
      type: "actions",
      getActions: (params) => {
        let arr = [];
        arr.push(
          <GridActionsCellItem
            icon={<Edit />}
            onClick={() => editOwnersClicked(params.row)}
            label="Edit"
            showInMenu
          />,
        );
        arr.push(
          <GridActionsCellItem
            icon={<Visibility />}
            onClick={() => handleViewClick(params.row)}
            label="View"
            showInMenu
          />,
        );

        if (params.row.type === "Company" && !isEmptyObject(params.row.contact)) {
          arr.push(
            <GridActionsCellItem
              icon={<Contacts />}
              onClick={() => handleEditCompanyContactClicked(params.row)}
              label="Edit Company Contact"
              showInMenu
            />,
          );
        }

        return arr;
      },
    },
  ];

  const handleViewClick = (item) => {
    setSelectedOwner(item);
    setViewOwnerModal(true);
  };

  function editOwnersClicked(item) {
    setTask("Edit");
    setSelectedOwner(item);
    if (item.type === "Company") {
      setAddOrganizationModal(true);
    }
    if (item.type === "Individual") {
      setAddUserModal(true);
    }
  }

  function handleAddOrgClose() {
    setAddOrganizationModal(false);
    setTask("");
    setLoading(true)
    fetchOwners()
  }

  function handleAddUserClose() {
    setAddUserModal(false);
    setTask("");
  }

  function handleUpdateOwner(data) {
    saveOwner(data).then((response) => {
      if (response) {
        issueResponseMessage(response);
        if (response.type !== "error") {
          handleAddUserClose();
          fetchOwners()
        }
      }
    });
  }

  function handleEditCompanyContactClicked(item) {
    setEditCompanyContactModal(true);
    setSelectedCompanyContact(item.contact);
    setTask("Edit");
  }

  function validateUserForm(sendData) {
    if (isNullUndefined(sendData)) {
      issueWarnMessage(
        "Could not find user data, please refresh the page and try again.",
      );
      return false;
    }
    if (isEmptyString(sendData.firstName) || isEmptyString(sendData.lastName)) {
      issueWarnMessage("First name and Last name are required.");
      return false;
    }
    if (
      isNullUndefined(sendData.mobilePhone) ||
      isEmptyString(sendData.mobilePhone)
    ) {
      issueWarnMessage("Mobile Phone number is required.");
      return false;
    }
    const { isValid, errors } = isValidPhoneNumber(sendData?.mobilePhone);
    if (!isValid) {
      issueWarnMessage(`Mobile phone number is invalid. ${errors.join(". ")}`);
      return false;
    }

    if (sendData?.officePhone) {
      if (sendData?.officePhone === sendData?.mobilePhone) {
        issueWarnMessage(
          `Office phone number cannot be same as mobile phone number. Leave blank if you don't have a separate phone number.`,
        );
        return false;
      }
      const { isValid, errors } = isValidPhoneNumber(sendData?.officePhone);
      if (!isValid) {
        issueWarnMessage(
          `Office phone number is invalid. ${errors.join(". ")}`,
        );
        return false;
      }
    }
    if (
      isNullUndefined(sendData.idNumber) ||
      isEmptyString(sendData.idNumber) ||
      isNullUndefined(sendData.idTypeCd) ||
      isEmptyString(sendData.idTypeCd)
    ) {
      issueWarnMessage("Must provide and ID number and ID type.");
      return false;
    }
    if (isNullUndefined(sendData.email) || isEmptyString(sendData.email)) {
      issueWarnMessage("Must provide an email.");
      return false;
    }

    return true;
  }

  function handleSaveCompanyContactClicked() {
    let params = selectedCompanyContact;
    if (validateUserForm(params)) {
      saveUser(params).then((response) => {
        issueResponseMessage(response);
        fetchOwners()
        setEditCompanyContactModal(false);
        setSelectedCompanyContact();
      });
    }
  }

  async function fetchOwners() {
    setLoading(true);
    try {
      await Promise.all([
        loadOwners(ownerName, paginationModel.page, paginationModel.pageSize).then(setOwnerList),
        totalOwners(ownerName).then((data) => setTotalOwners(data.total))
      ]);
    } catch (error) {
      console.error("Failed to fetch owners:", error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Permit
      roles="BLDIR,BLDMG,ACC,CS"
      noPermittedServiceMessage={{
        title: "Access Denied",
        body: (
          <>
            Your current role is not permitted to view this page. <br /> Please
            contact your system admin if you feel this is an error.
          </>
        ),
      }}
    >
      <MainContainer>
        {task !== "Edit" && (
          <Grid
            container
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: { lg: "nowrap", md: "wrap" },
              justifyContent: "space-between",
              width: { xs: "100%", md: "80%" },
              backgroundColor: "white",
              padding: { md: "1vh", xs: "1vh" },
              borderRadius: "1vh",
              margin: "20px auto",
              boxShadow: "0 4px 5px 0 rgba(0,0,0,0.5)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xl: "row", xs: "column" },
                justifyContent: { md: "space-between", xs: "center" },
                width: { lg: "33.3%", md: "50%", sm: "100%" },
              }}
              component="form"
            >
              <FormControl
                fullWidth
                sx={{
                  margin: { md: "auto 20px", xs: "10px auto " },
                  width: "100%",
                }}
              >
                <InputLabel sx={{ fontSize: ".9rem" }} id="ownerLabel">
                  Search Home owner's name
                </InputLabel>
                <OutlinedInput
                  id="ownerNameSearch"
                  value={ownerName}
                  label={`Search home owner's name`}
                  sx={{ width: "100%" }}
                  onChange={(event) => {
                    let value = event.target.value;
                    setOwnerName(value);
                  }}
                />
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xl: "row", xs: "column" },
                justifyContent: { md: "space-between", xs: "center" },
                width: { md: "20%", xs: "100%" },
                padding: "10px",
              }}
              component="form"
            ></Box>
          </Grid>
        )}

        {task !== "Edit" && (
          <MuiDataGrid
            noSelection={true}
            dataGridColumns={columns}
            dataGridRows={ownerList}
            serverPagination={true}
            handlePageLoad={setPaginationModel}
            loading={loading}
            height="60vh"
            totalRows={total}
          />
        )}

        {task === "Edit" && selectedOwner.type === "Company" && (
          <AddNewOrganization
            openModal={addOrganizationModal}
            closeModal={handleAddOrgClose}
            allowEditOrg={true}
            task={task}
            homeOwner={true}
            selectedOrg={selectedOwner}
          />
        )}

        {task === "Edit" && selectedOwner.type === "Individual" && (
          <AddNewUser
            openModal={addUserModal}
            closeModal={handleAddUserClose}
            saveUser={handleUpdateOwner}
            manageUsers={false}
            allowEditUser={true}
            task={task}
            homeOwner={true}
            selectedPerson={selectedOwner}
          />
        )}

        {viewOwnerModal && (
          <ViewOwnerModal
            openModal={viewOwnerModal}
            closeModal={() => setViewOwnerModal(false)}
            ownerToView={selectedOwner}
          />
        )}

        <EditUserModal
          showAddNewUserForm={editCompanyContactModal}
          handleAddUserClose={() => {
            setEditCompanyContactModal(false);
            setSelectedCompanyContact();
            setTask("");
          }}
          selectedUser={selectedCompanyContact}
          setSelectedUser={(userUpdate) =>
            setSelectedCompanyContact(userUpdate)
          }
          handleSaveUserClicked={handleSaveCompanyContactClicked}
          idTypes={idTypes}
          userRoles={userRoles}
          isCompanyContact={true}
          task={task}
        />
      </MainContainer>
    </Permit>
  );
}
